import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";
import { normalizeBlobError, parseContentDispositionFilename } from "@/shared/utils/downloadBlob";

import type {
  DownloadedFile,
  ListPayoutBatchesParams,
  ListPayoutBatchItemsParams,
  PaginatedPage,
  PayoutBatch,
  PayoutBatchDetail,
  PayoutBatchEvent,
  PayoutBatchItem,
  PayoutBatchSelectionOp,
  PayoutBatchStats,
  RetryPreview,
  UploadPayoutBatchInput,
} from "../types/payoutBatches.types";

const TWO_FACTOR_HEADER = "x-two-factor-code";

const twoFactorHeaders = (code: string) => ({
  headers: { [TWO_FACTOR_HEADER]: code },
});

const toQuery = (params: Record<string, string | number | boolean | undefined>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `?${text}` : "";
};

async function downloadXlsx(url: string, fallbackName: string): Promise<DownloadedFile> {
  try {
    const response = await HttpApiService.get<Blob>(url, {
      responseType: "blob",
    });
    const header = (response.headers as Record<string, string | undefined>)["content-disposition"];
    return {
      blob: response.data,
      filename: parseContentDispositionFilename(header) ?? fallbackName,
    };
  } catch (error: unknown) {
    throw await normalizeBlobError(error);
  }
}

/**
 * `/internal/payments/payout-batches` — the only place that knows the wire: multipart upload, the
 * `x-two-factor-code` header on the four step-up mutations, blob downloads and the query spelling. Hooks read
 * `response.data`; components never call this directly.
 */
export class PayoutBatchesService {
  private static BASE_URL = "/internal/payments/payout-batches";

  static async downloadTemplate(country: string, rail: string): Promise<DownloadedFile> {
    return downloadXlsx(
      `${this.BASE_URL}/template${toQuery({ country, rail })}`,
      `payout-batch-template-${country}-${rail}.xlsx`,
    );
  }

  static async upload(
    input: UploadPayoutBatchInput,
    twoFactorCode: string,
  ): Promise<AxiosResponse<PayoutBatchDetail>> {
    const form = new FormData();
    form.append("file", input.file, input.file.name);
    form.append("source_currency", input.sourceCurrency);
    form.append("country", input.country);
    form.append("rail", input.rail);
    if (input.concept?.trim()) form.append("concept", input.concept.trim());
    if (input.organizationId) form.append("organization_id", input.organizationId);
    return HttpApiService.post<PayoutBatchDetail, FormData>(
      this.BASE_URL,
      form,
      twoFactorHeaders(twoFactorCode),
    );
  }

  static async list(params: ListPayoutBatchesParams): Promise<AxiosResponse<PaginatedPage<PayoutBatch>>> {
    return HttpApiService.get<PaginatedPage<PayoutBatch>>(
      `${this.BASE_URL}${toQuery({
        skip: params.skip,
        limit: params.limit,
        status: params.status,
        organization_id: params.organizationId,
        rail: params.rail,
        country: params.country,
        from: params.from,
        to: params.to,
      })}`,
    );
  }

  static async stats(organizationId?: string): Promise<AxiosResponse<PayoutBatchStats>> {
    return HttpApiService.get<PayoutBatchStats>(
      `${this.BASE_URL}/stats${toQuery({ organization_id: organizationId })}`,
    );
  }

  static async getOne(id: string): Promise<AxiosResponse<PayoutBatchDetail>> {
    return HttpApiService.get<PayoutBatchDetail>(`${this.BASE_URL}/${id}`);
  }

  static async listItems(
    id: string,
    params: ListPayoutBatchItemsParams,
  ): Promise<AxiosResponse<PaginatedPage<PayoutBatchItem>>> {
    return HttpApiService.get<PaginatedPage<PayoutBatchItem>>(
      `${this.BASE_URL}/${id}/items${toQuery({
        skip: params.skip,
        limit: params.limit,
        status: params.status,
        has_errors: params.hasErrors,
        has_warnings: params.hasWarnings,
      })}`,
    );
  }

  static async listEvents(
    id: string,
    params: { skip: number; limit: number },
  ): Promise<AxiosResponse<PaginatedPage<PayoutBatchEvent>>> {
    return HttpApiService.get<PaginatedPage<PayoutBatchEvent>>(
      `${this.BASE_URL}/${id}/events${toQuery({ skip: params.skip, limit: params.limit })}`,
    );
  }

  static async getFileUrl(id: string): Promise<AxiosResponse<{ url: string }>> {
    return HttpApiService.get<{ url: string }>(`${this.BASE_URL}/${id}/file`);
  }

  static async downloadReport(id: string): Promise<DownloadedFile> {
    return downloadXlsx(`${this.BASE_URL}/${id}/report`, `payout-batch-${id.slice(0, 8)}-report.xlsx`);
  }

  static async updateSelection(
    id: string,
    op: PayoutBatchSelectionOp,
    itemIds?: string[],
  ): Promise<AxiosResponse<PayoutBatchDetail>> {
    return HttpApiService.patch<PayoutBatchDetail, { op: PayoutBatchSelectionOp; item_ids?: string[] }>(
      `${this.BASE_URL}/${id}/selection`,
      itemIds ? { op, item_ids: itemIds } : { op },
    );
  }

  static async revalidate(id: string): Promise<AxiosResponse<PayoutBatchDetail>> {
    return HttpApiService.post<PayoutBatchDetail, undefined>(`${this.BASE_URL}/${id}/revalidate`, undefined);
  }

  static async submit(id: string, fingerprint: string): Promise<AxiosResponse<PayoutBatchDetail>> {
    return HttpApiService.post<PayoutBatchDetail, { fingerprint: string }>(`${this.BASE_URL}/${id}/submit`, {
      fingerprint,
    });
  }

  static async reject(id: string, reason: string): Promise<AxiosResponse<PayoutBatchDetail>> {
    return HttpApiService.post<PayoutBatchDetail, { reason: string }>(`${this.BASE_URL}/${id}/reject`, {
      reason: reason.trim(),
    });
  }

  static async withdraw(id: string): Promise<AxiosResponse<PayoutBatchDetail>> {
    return HttpApiService.post<PayoutBatchDetail, Record<string, never>>(
      `${this.BASE_URL}/${id}/withdraw`,
      {},
    );
  }

  static async execute(
    id: string,
    input: { fingerprint: string; acknowledgeProviderBalance?: boolean },
    twoFactorCode: string,
  ): Promise<AxiosResponse<PayoutBatchDetail>> {
    return HttpApiService.post<
      PayoutBatchDetail,
      { fingerprint: string; acknowledge_provider_balance?: boolean }
    >(
      `${this.BASE_URL}/${id}/execute`,
      input.acknowledgeProviderBalance
        ? { fingerprint: input.fingerprint, acknowledge_provider_balance: true }
        : { fingerprint: input.fingerprint },
      twoFactorHeaders(twoFactorCode),
    );
  }

  static async retryPreview(id: string, itemIds: string[]): Promise<AxiosResponse<RetryPreview>> {
    return HttpApiService.post<RetryPreview, { item_ids: string[] }>(
      `${this.BASE_URL}/${id}/items/retry/preview`,
      {
        item_ids: itemIds,
      },
    );
  }

  static async retryItems(
    id: string,
    input: { itemIds: string[]; maxTotalDebit: string },
    twoFactorCode: string,
  ): Promise<AxiosResponse<PayoutBatchDetail>> {
    return HttpApiService.post<PayoutBatchDetail, { item_ids: string[]; max_total_debit: string }>(
      `${this.BASE_URL}/${id}/items/retry`,
      { item_ids: input.itemIds, max_total_debit: input.maxTotalDebit },
      twoFactorHeaders(twoFactorCode),
    );
  }

  static async resume(id: string, twoFactorCode: string): Promise<AxiosResponse<PayoutBatchDetail>> {
    return HttpApiService.post<PayoutBatchDetail, undefined>(
      `${this.BASE_URL}/${id}/resume`,
      undefined,
      twoFactorHeaders(twoFactorCode),
    );
  }

  static async cancel(id: string, reason?: string): Promise<AxiosResponse<PayoutBatchDetail>> {
    const trimmed = reason?.trim();
    return HttpApiService.post<PayoutBatchDetail, { reason?: string }>(
      `${this.BASE_URL}/${id}/cancel`,
      trimmed ? { reason: trimmed } : {},
    );
  }
}
